import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Play, X, Pause, RotateCcw } from 'lucide-react';
import { ReelTemplate } from './ReelTemplates';

interface TemplatePreviewProps {
  template: ReelTemplate;
  onClose: () => void;
}

// Extended sample content for 1-minute preview
// Slide durations - vary between 1-2 seconds for rapid subliminal effect
const SLIDE_DURATIONS = [1000, 1200, 1500, 1800, 2000]; // milliseconds

const SAMPLE_IMAGES = [
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80',
  'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=1200&q=80',
  'https://images.unsplash.com/photo-1511884642898-4c92249e20b6?w=1200&q=80',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200&q=80',
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80',
  'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1200&q=80',
  'https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?w=1200&q=80',
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80',
];

const SAMPLE_AFFIRMATIONS = [
  'I am manifesting my dreams',
  'Abundance flows to me',
  'I am worthy of success',
  'My future is limitless',
  'I attract prosperity',
  'I am my highest self',
  'Everything comes to me',
  'I radiate confidence'
];

const IMAGE_TRANSITIONS = [
  'pan-right',
  'pan-left',
  'zoom-in-fast',
  'zoom-out',
  'slide-from-left',
  'slide-from-right',
  'rotate-zoom',
  'diagonal-slide',
  'fade-scale'
];

const TEXT_TRANSITIONS = [
  'pop-in',
  'slide-up-bounce',
  'fade-glow',
  'scale-burst',
  'flip-in',
  'slide-from-side',
  'glitch-in',
  'wave-in'
];

export default function TemplatePreview({ template, onClose }: TemplatePreviewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const [currentDuration, setCurrentDuration] = useState(SLIDE_DURATIONS[0]);

  const currentImage = SAMPLE_IMAGES[currentIndex % SAMPLE_IMAGES.length];
  const currentAffirmation = SAMPLE_AFFIRMATIONS[currentIndex % SAMPLE_AFFIRMATIONS.length];
  
  // Calculate total slides to achieve 60 seconds with varied durations
  const totalSlides = 45; // ~1.3 seconds average = 60 seconds total

  // Dynamic animations based on transition type
  const getImageAnimationClass = () => {
    const transition = IMAGE_TRANSITIONS[currentIndex % IMAGE_TRANSITIONS.length];
    const duration = currentDuration;
    const animationMap: Record<string, string> = {
      'pan-right': `animate-[pan-right_${duration}ms_linear]`,
      'pan-left': `animate-[pan-left_${duration}ms_linear]`,
      'zoom-in-fast': `animate-[zoom-in-fast_${duration}ms_ease-out]`,
      'zoom-out': `animate-[zoom-out_${duration}ms_ease-in]`,
      'slide-from-left': `animate-[slide-from-left_${duration}ms_ease-out]`,
      'slide-from-right': `animate-[slide-from-right_${duration}ms_ease-out]`,
      'rotate-zoom': `animate-[rotate-zoom_${duration}ms_ease-in-out]`,
      'diagonal-slide': `animate-[diagonal-slide_${duration}ms_ease-out]`,
      'fade-scale': `animate-[fade-scale_${duration}ms_ease-out]`
    };
    return animationMap[transition] || '';
  };

  const getTextAnimationClass = () => {
    const transition = TEXT_TRANSITIONS[currentIndex % TEXT_TRANSITIONS.length];
    const animationMap: Record<string, string> = {
      'pop-in': 'animate-[pop-in_0.4s_cubic-bezier(0.68,-0.55,0.265,1.55)]',
      'slide-up-bounce': 'animate-[slide-up-bounce_0.5s_cubic-bezier(0.68,-0.55,0.265,1.55)]',
      'fade-glow': 'animate-[fade-glow_0.4s_ease-out]',
      'scale-burst': 'animate-[scale-burst_0.5s_ease-out]',
      'flip-in': 'animate-[flip-in_0.5s_ease-out]',
      'slide-from-side': 'animate-[slide-from-side_0.4s_ease-out]',
      'glitch-in': 'animate-[glitch-in_0.5s_ease-out]',
      'wave-in': 'animate-[wave-in_0.6s_ease-out]'
    };
    return animationMap[transition] || '';
  };

  // Progress bar animation
  useEffect(() => {
    if (isPlaying) {
      const updateProgress = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const totalDuration = 60000; // 60 seconds
        const newProgress = Math.min((elapsed / totalDuration) * 100, 100);
        setProgress(newProgress);
        
        if (newProgress < 100) {
          progressRef.current = setTimeout(updateProgress, 16); // ~60fps
        }
      };
      updateProgress();
    }

    return () => {
      if (progressRef.current) clearTimeout(progressRef.current);
    };
  }, [isPlaying, startTimeRef.current]);

  // Slide transitions with varied durations
  useEffect(() => {
    if (!isPlaying) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Pick a random duration for variety
    const duration = SLIDE_DURATIONS[currentIndex % SLIDE_DURATIONS.length];
    setCurrentDuration(duration);
    
    timerRef.current = setTimeout(() => {
      if (currentIndex < totalSlides - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setIsPlaying(false);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    }, duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentIndex, isPlaying, totalSlides, currentDuration]);

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      startTimeRef.current = Date.now() - (progress / 100 * 60000);
      setIsPlaying(true);
    }
  };

  const restart = () => {
    // Clear all timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (progressRef.current) {
      clearTimeout(progressRef.current);
      progressRef.current = null;
    }
    // Reset state
    setCurrentIndex(0);
    setProgress(0);
    setIsPlaying(false);
    startTimeRef.current = Date.now();
    // Start playing after a brief delay to ensure clean state
    setTimeout(() => setIsPlaying(true), 50);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (progressRef.current) {
        clearTimeout(progressRef.current);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      <div className="relative w-full max-w-3xl aspect-[9/16] rounded-xl overflow-hidden shadow-2xl">
        {/* Background Image with Dynamic Animation */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            key={`image-${currentIndex}`}
            className={`absolute inset-0 ${getImageAnimationClass()}`}
            style={{
              backgroundImage: `url(${currentImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              willChange: 'transform'
            }}
          />
          
          {/* Cinematic Gradient Overlays */}
          <div className={`absolute inset-0 bg-gradient-to-b ${template.colors.primary} opacity-40 mix-blend-multiply`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />
          <div className={`absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/30`} />
        </div>

        {/* Template Info Header */}
        <div className="absolute top-0 left-0 right-0 p-6 bg-gradient-to-b from-black/80 via-black/40 to-transparent z-20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-white font-bold text-xl mb-1.5 drop-shadow-lg">{template.name}</h3>
              <p className="text-white/90 text-sm drop-shadow-md">{template.description}</p>
              <div className="flex gap-2 mt-2">
                <div className="bg-white/20 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/20">
                  <p className="text-white text-xs font-semibold">⚡ Subliminal Speed</p>
                </div>
                <div className="bg-white/20 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/20">
                  <p className="text-white text-xs font-semibold">{currentDuration}ms</p>
                </div>
              </div>
            </div>
            <Button
              onClick={onClose}
              size="icon"
              variant="ghost"
              className="text-white hover:bg-white/20 backdrop-blur-sm"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Animated Affirmation - Rapid Fire */}
        <div className="absolute inset-0 flex items-center justify-center p-8 z-10">
          <div
            key={`text-${currentIndex}`}
            className="text-center"
          >
            <p
              className={`text-white text-3xl md:text-5xl lg:text-6xl font-black leading-tight ${getTextAnimationClass()} ${template.colors.accent}`}
              style={{
                textShadow: '0 0 40px rgba(0,0,0,0.8), 0 0 80px rgba(0,0,0,0.5)',
                letterSpacing: '-0.02em'
              }}
            >
              {currentAffirmation}
            </p>
          </div>
        </div>

        {/* Subliminal text layers - scattered across screen */}
        <div className="absolute top-8 left-8 text-white/15 text-xs font-mono opacity-40 z-5">
          I am abundant
        </div>
        <div className="absolute bottom-24 right-12 text-white/15 text-xs font-mono opacity-40 z-5">
          Success flows
        </div>
        <div className="absolute top-1/3 right-16 text-white/10 text-[10px] font-mono opacity-30 -rotate-12 z-5">
          Limitless potential
        </div>
        <div className="absolute bottom-1/3 left-20 text-white/10 text-[10px] font-mono opacity-30 rotate-6 z-5">
          Worthy of everything
        </div>

        {/* Simplified progress - single bar for rapid transitions */}
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 w-3/4 max-w-md z-20">
          <div className="h-1 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / totalSlides) * 100}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-20">
          {/* Progress Bar */}
          <div className="w-full h-1 bg-white/20 rounded-full mb-4 overflow-hidden backdrop-blur-sm">
            <div
              className="h-full bg-gradient-to-r from-white via-primary to-accent transition-all duration-100 shadow-glow"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={restart}
              size="icon"
              variant="ghost"
              className="text-white hover:bg-white/20 backdrop-blur-sm h-11 w-11"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
            
            <Button
              onClick={togglePlay}
              size="icon"
              variant="ghost"
              className="text-white hover:bg-white/20 backdrop-blur-sm h-14 w-14 border-2 border-white/30"
            >
              {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
            </Button>
          </div>

          {/* Info tags */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="bg-white/15 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
              <p className="text-white text-xs font-semibold">
                {IMAGE_TRANSITIONS[currentIndex % IMAGE_TRANSITIONS.length].replace(/-/g, ' ').toUpperCase()}
              </p>
            </div>
            <div className="bg-white/15 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
              <p className="text-white text-xs font-semibold">
                {currentIndex + 1}/{totalSlides} • {Math.round(progress)}%
              </p>
            </div>
          </div>
        </div>

        {/* Decorative corner elements */}
        <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl ${template.colors.secondary} opacity-20 rounded-bl-[100px] blur-2xl`} />
        <div className={`absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr ${template.colors.secondary} opacity-20 rounded-tr-[100px] blur-2xl`} />
      </div>
    </div>
  );
}
